import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Receipt } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const fmtMoney = (n, currency = 'AUD') => new Intl.NumberFormat('en-AU', { style: 'currency', currency }).format(n || 0);

export default function PublicInvoice() {
  const { token } = useParams();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/share/invoice/${token}`)
      .then((r) => setInv(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Loading…</p></div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><p className="text-slate-500">Invoice not available.</p></div>;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 print:bg-white print:py-0">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 print:mb-3">
          <div className="flex items-center gap-3">
            <Receipt className="w-8 h-8 text-emerald-600" />
            <div>
              <p className="text-xs text-slate-500">Invoice · {inv.number}</p>
              <h1 className="text-2xl font-bold">{inv.title || 'Invoice'}</h1>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="text-slate-500">Issued: <b>{inv.issue_date}</b></p>
            <p className="text-slate-500">Due: <b>{inv.due_date}</b></p>
            <p className={`mt-1 inline-block text-xs px-2 py-0.5 rounded ${inv.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : inv.status === 'overdue' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>{inv.status}</p>
          </div>
        </div>

        <Card className="p-8 mb-4 print:shadow-none print:border-0">
          {inv.client && <p className="text-sm text-slate-600 mb-3"><b>Bill to:</b> {inv.client.name}{inv.client.address && <> · {inv.client.address}</>}</p>}
          {inv.intro_html && <div className="prose prose-sm max-w-none mb-4" dangerouslySetInnerHTML={{ __html: inv.intro_html }} />}

          <table className="w-full text-sm mt-4">
            <thead className="text-left text-xs text-slate-500 border-b">
              <tr><th className="py-2">Description</th><th className="py-2 w-20">Qty</th><th className="py-2 w-28">Unit price</th><th className="py-2 w-28 text-right">Total</th></tr>
            </thead>
            <tbody>
              {(inv.items || []).map((it) => (
                <tr key={it.id} className="border-b last:border-0">
                  <td className="py-2">{it.description}</td>
                  <td className="py-2">{it.quantity} {it.unit}</td>
                  <td className="py-2">{fmtMoney(it.unit_price, inv.currency)}</td>
                  <td className="py-2 text-right font-medium">{fmtMoney((it.quantity || 0) * (it.unit_price || 0), inv.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 ml-auto w-fit text-sm space-y-1">
            <div className="flex justify-between gap-8"><span className="text-slate-500">Subtotal:</span><span>{fmtMoney(inv.subtotal, inv.currency)}</span></div>
            {inv.discount > 0 && <div className="flex justify-between gap-8"><span className="text-slate-500">Discount:</span><span>−{fmtMoney(inv.discount, inv.currency)}</span></div>}
            <div className="flex justify-between gap-8"><span className="text-slate-500">Tax ({(inv.tax_rate * 100).toFixed(0)}%):</span><span>{fmtMoney(inv.tax, inv.currency)}</span></div>
            <div className="flex justify-between gap-8 text-lg pt-2 border-t"><span className="font-semibold">Total:</span><span className="font-bold">{fmtMoney(inv.total, inv.currency)}</span></div>
            {inv.paid > 0 && <div className="flex justify-between gap-8 text-emerald-700"><span>Paid:</span><span className="font-medium">{fmtMoney(inv.paid, inv.currency)}</span></div>}
            {inv.balance > 0 && <div className="flex justify-between gap-8 text-amber-700"><span>Balance due:</span><span className="font-bold">{fmtMoney(inv.balance, inv.currency)}</span></div>}
          </div>
        </Card>

        <div className="text-center print:hidden">
          <button onClick={() => window.print()} className="text-sm text-emerald-600 hover:underline">Print / Save as PDF</button>
        </div>
      </div>
    </div>
  );
}
